import { Translate } from '@dreryk/react-common';
import { StyledFlex } from '@dreryk/react-components';

import { DosageStageValidation, FrequencyDosageCycle } from '~libs.shared.prescription';

import { prescriptionTranslationKeys } from '../../../../../../../translations';
import { ExtendedDosageFormController } from '../ExtendedDosageForm.types';
import { createFrequencyDosageCycle, DOSAGE_TIME_UNITS } from '../../extendedDosage.utils';

import { DosagePeriodField } from './DosagePeriodField';
import { StyledStagePanel } from './StageField.styled';
import { PortionNumberInput } from './PortionNumberInput';
import { createStageFieldNameFactory } from './StageFieldList.utils';
import { useDurationUnitLock, useFrequencyDosageCycleAmountSteps } from './FrequencyDosageCycleField.hooks';

const DEFAULTS = createFrequencyDosageCycle();

type FrequencyDosageCycleFieldProps = Pick<
   ExtendedDosageFormController,
   'register' | 'watch' | 'setValue' | 'trigger'
> & {
   readonly field: Partial<FrequencyDosageCycle>;
   readonly index: number;
   readonly validation: DosageStageValidation;
   readonly portionUnit: string | null;
};

export const FrequencyDosageCycleField = (props: FrequencyDosageCycleFieldProps): JSX.Element => {
   const createFieldName = createStageFieldNameFactory(props.index);
   const intervalUnitDefaultValue = props.field.interval?.unit ?? DEFAULTS.interval.unit;
   const intervalAmountDefaultValue = Number(props.field.interval?.amount ?? DEFAULTS.interval.amount);
   const { durationUnitLocked, handleIntervalUnitSelect } = useDurationUnitLock({
      watch: props.watch,
      setValue: props.setValue,
      index: props.index,
      intervalUnitDefaultValue: intervalUnitDefaultValue,
   });
   const { intervalAmountSteps, durationAmountSteps } = useFrequencyDosageCycleAmountSteps({
      watch: props.watch,
      index: props.index,
      intervalUnitDefaultValue: intervalUnitDefaultValue,
      intervalAmountDefaultValue: intervalAmountDefaultValue,
   });

   return (
      <StyledStagePanel>
         <StyledFlex direction={'row'} flexWrap={'wrap'} alignItems={'center'} gap={'6px'}>
            <Translate>{prescriptionTranslationKeys.extendedDosage.administer}</Translate>
            <PortionNumberInput
               register={props.register}
               name={createFieldName('portion')}
               defaultValue={Number(props.field.portion ?? DEFAULTS.portion)}
               testId={`portion-${props.index}`}
            />
            {props.portionUnit}
            <Translate>{prescriptionTranslationKeys.extendedDosage.interval}</Translate>
            <DosagePeriodField
               register={props.register}
               watch={props.watch}
               trigger={props.trigger}
               amountName={createFieldName('interval.amount')}
               unitName={createFieldName('interval.unit')}
               units={DOSAGE_TIME_UNITS}
               amountDefaultValue={intervalAmountDefaultValue}
               unitDefaultValue={intervalUnitDefaultValue}
               amountSteps={intervalAmountSteps}
               onUnitSelect={handleIntervalUnitSelect}
               invalid={
                  props.validation.frequencyNotAllowed ||
                  props.validation.durationNotMultipleOfIntervalAmount !== undefined
               }
               testId={`interval-${props.index}`}
            />
            <Translate>{prescriptionTranslationKeys.extendedDosage.during}</Translate>
            <DosagePeriodField
               register={props.register}
               watch={props.watch}
               trigger={props.trigger}
               amountName={createFieldName('duration.amount')}
               unitName={createFieldName('duration.unit')}
               amountDefaultValue={Number(props.field.duration?.amount ?? DEFAULTS.duration.amount)}
               unitDefaultValue={props.field.duration?.unit ?? DEFAULTS.duration.unit}
               unitDisabled={durationUnitLocked}
               amountSteps={durationAmountSteps}
               invalid={
                  props.validation.durationNotMultipleOfIntervalAmount !== undefined ||
                  props.validation.exceededPackagesCount !== undefined
               }
               testId={`duration-${props.index}`}
            />
         </StyledFlex>
      </StyledStagePanel>
   );
};
